import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Clock, Shield, MapPin, Car, ArrowRight } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

const AutooeffnungPage = () => {
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';

  const faqData = [
    {
      question: 'Öffnen Sie auch nachts und am Wochenende?',
      answer: 'Ja, unser Autoöffnungs-Service ist 24/7 verfügbar – auch nachts, am Wochenende und an Feiertagen. Rufen Sie uns einfach an!'
    },
    {
      question: 'Können bei der Öffnung Schäden entstehen?',
      answer: 'Wir verwenden professionelles Spezialwerkzeug für PKW-Öffnungen und arbeiten sehr sorgfältig. In den allermeisten Fällen gelingt die Öffnung ohne jede Beschädigung.'
    },
    {
      question: 'Benötigen Sie Fahrzeugpapiere?',
      answer: 'Ja, aus Sicherheitsgründen benötigen wir einen Nachweis, dass Sie der Halter oder berechtigte Nutzer des Fahrzeugs sind – z.B. Fahrzeugschein, Personalausweis oder Führerschein.'
    },
    {
      question: 'Welche Automarken können Sie öffnen?',
      answer: 'Wir öffnen PKW aller gängigen Marken und Modelle. Egal ob deutsches, europäisches oder asiatisches Fahrzeug – wir haben die passenden Werkzeuge.'
    },
    {
      question: 'Was kostet eine Autoöffnung?',
      answer: 'Die Kosten hängen vom Fahrzeugmodell und der Tageszeit ab. Wir nennen Ihnen den Preis bereits am Telefon, bevor wir kommen.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Autoöffnung in Dortmund – PKW öffnen lassen ohne Schaden</title>
        <meta name="description" content="Auto zu, Schlüssel im Fahrzeug oder verloren? Schlüsseldienst Dortmund öffnet PKW professionell & möglichst schadenfrei. Jetzt anrufen: 0231 39085116." />
        <link rel="canonical" href="https://schluesseldienst.services/leistungen/autooeffnung/" />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs items={[
          { label: 'Leistungen', path: '/leistungen' },
          { label: 'Autoöffnung' }
        ]} />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8 text-gray-900">
          Autoöffnung in Dortmund – wenn der Autoschlüssel fehlt
        </h1>

        <div className="max-w-4xl mb-12">
          <p className="text-lg text-gray-700 mb-4">
            <strong>Schlüssel im Auto eingesperrt? Kofferraum zentral verriegelt?</strong> Diese Situation ist ärgerlich, 
            aber kein Grund zur Panik. Als spezialisierter Schlüsseldienst in Dortmund helfen wir Ihnen bei der professionellen 
            Autoöffnung – schnell, diskret und möglichst ohne Beschädigung.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Ob Sie den Schlüssel versehentlich im Fahrzeug gelassen haben, das Auto sich zentral verriegelt hat oder 
            der Schlüssel verloren gegangen ist – wir haben das richtige Werkzeug und die Erfahrung, um Ihren PKW 
            fachgerecht zu öffnen.
          </p>
          <p className="text-lg text-gray-700 mb-6">
            Die Schössow Dienstleistungen UG ist 24 Stunden am Tag für Sie erreichbar. Wir kommen schnell zu Ihnen, 
            egal wo Sie in Dortmund sind – ob auf dem Parkplatz, vor der Haustür oder unterwegs.
          </p>
          
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Auto verschlossen? Sofort Hilfe!</h3>
                <p className="text-gray-700">Wir sind in 15-30 Minuten bei Ihnen in Dortmund</p>
              </div>
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold text-lg transition-all shadow-lg whitespace-nowrap"
              >
                <Phone size={20} />
                {displayPhone}
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Vorteile auf einen Blick</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Spezielles PKW-Werkzeug</strong> für verschiedene Fahrzeugtypen</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Möglichst schadenfreie Öffnung</strong> – mit Sorgfalt und Erfahrung</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Transparente Preise</strong> – vorab am Telefon besprochen</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Schnelle Anfahrt</strong> in ganz Dortmund</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>24/7 verfügbar</strong> – auch nachts und am Wochenende</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Diskreter Service</strong> – professionell und unauffällig</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Welche Fahrzeuge öffnen wir?</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex gap-3 mb-4">
              <Car className="w-6 h-6 text-orange-600 flex-shrink-0" />
              <div>
                <p className="text-gray-700 mb-3">
                  <strong>Wir öffnen PKW aller gängigen Marken:</strong> Egal ob VW, Audi, BMW, Mercedes, Opel, 
                  Ford, Renault, Peugeot, Toyota, Nissan oder andere Marken – wir haben das passende Werkzeug 
                  und die Erfahrung für die meisten Fahrzeugmodelle.
                </p>
                <p className="text-gray-700">
                  Auch bei neueren Modellen mit Zentralverriegelung und elektronischen Systemen können wir 
                  in vielen Fällen helfen. Rufen Sie uns an und schildern Sie kurz Ihr Fahrzeugmodell – 
                  wir beraten Sie gerne.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Preise & Ablauf</h2>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border-2 border-orange-200">
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  So läuft es ab
                </h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex gap-3">
                    <span className="font-bold text-orange-600">1.</span>
                    <span><strong>Anruf:</strong> Sie rufen uns unter {displayPhone} an und beschreiben Ihr Problem (Fahrzeugmodell, Standort)</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-orange-600">2.</span>
                    <span><strong>Preisinfo:</strong> Wir nennen Ihnen den voraussichtlichen Preis bereits am Telefon</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-orange-600">3.</span>
                    <span><strong>Schnelle Anfahrt:</strong> Wir kommen innerhalb von 15-30 Minuten zu Ihrem Standort</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-orange-600">4.</span>
                    <span><strong>Öffnung:</strong> Mit Spezialwerkzeug öffnen wir Ihr Fahrzeug möglichst schadensfrei</span>
                  </li>
                </ol>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-xl font-bold mb-3">Faire Preise</h3>
                <p className="text-gray-700">
                  Die Kosten für eine Autoöffnung hängen vom Fahrzeugmodell, der Sicherheitsausstattung und 
                  der Tageszeit ab. Wir besprechen den Preis vorab transparent mit Ihnen am Telefon. 
                  Keine versteckten Kosten!
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Sicherheit & Nachweis</h2>
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
            <div className="flex gap-3">
              <Shield className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">Legitimation erforderlich</h3>
                <p className="text-gray-700 mb-3">
                  Aus Sicherheitsgründen benötigen wir einen Nachweis, dass Sie der Halter oder 
                  berechtigte Nutzer des Fahrzeugs sind. Dies dient Ihrem Schutz und verhindert 
                  unbefugte Fahrzeugöffnungen.
                </p>
                <p className="text-gray-700">
                  <strong>Bitte halten Sie bereit:</strong> Fahrzeugschein (Zulassungsbescheinigung Teil I), 
                  Personalausweis oder alternativ Führerschein und weitere Nachweise.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Einsatzgebiet in Dortmund</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex gap-3 mb-4">
              <MapPin className="w-6 h-6 text-orange-600 flex-shrink-0" />
              <p className="text-gray-700">
                <strong>Wir sind in ganz Dortmund mobil.</strong> Egal wo Ihr Fahrzeug steht – auf einem 
                Parkplatz, vor Ihrer Haustür, an der Tankstelle oder am Straßenrand – wir kommen zu Ihnen.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3 text-gray-900">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-900 text-white rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Auto verschlossen? Jetzt anrufen!</h2>
          <p className="text-lg mb-6 text-gray-300">
            24/7 Notdienst für Autoöffnungen in ganz Dortmund<br />
            Professionell • Diskret • Schadensfrei
          </p>
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full font-bold text-xl transition-all shadow-lg hover:shadow-xl"
          >
            <Phone size={24} />
            {displayPhone}
          </a>
        </div>

        <div className="mt-12 max-w-4xl">
          <h3 className="text-xl font-bold mb-4 text-gray-900">Weitere Leistungen</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/leistungen" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Alle Leistungen <ArrowRight size={18} />
            </Link>
            <Link to="/leistungen/tresoroeffnung" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Tresoröffnung <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default AutooeffnungPage;
